import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Box, Group, Image, Stack, Text, Tooltip } from "@mantine/core";
import type { CalendarEventType } from "../../../../../types/calendar";
import type { CalendarEvent } from "../../../../../types/transactions";
import { DESIGN_ICONS, timeBlockIcon } from "../../../../../theme/designIcons";
import { wallClockLabel } from "./gridParts";
import { laneMeta, laneOverflow } from "./laneLayout";
import { getSalonNow } from "./sharedCalendarUtils";
import styles from "./EventBlock.module.css";

/**
 * The one calendar event block for Day and Week (prototype `deco`):
 * service-tinted card, 4px service bar, service line above the client, mono
 * time, processing band, Request pill, past / unpaid / status looks, the
 * flag-chip slot, and the slate time block. Look is set by
 * `makeEventPropGetter` (classes + the --svc var on `.rbc-event`); content
 * by `EventBlock` (RBC `event` component, module level).
 */

export type BlockMode = "day" | "week";

interface EventBlockContextValue {
  mode: BlockMode;
  timezone?: string;
  /** Week Combined: the staff initials for the block's chip (else none). */
  initialsFor?: (event: CalendarEventType) => string | null;
  /** Week Combined: the staff photo + name for that chip (initials when none). */
  photoFor?: (event: CalendarEventType) => { src: string; name: string } | null;
  /** The block's flag chips (calendarFlags; none when absent). */
  flagsFor?: (event: CalendarEventType) => EventFlag[];
}

export const EventBlockContext = createContext<EventBlockContextValue>({
  mode: "day",
});

const FALLBACK_SERVICE_COLOR = "var(--brand-teal)";

/** 20 min or less: tight vertical padding so the one line fits. */
const SHORT_MS = 20 * 60 * 1000;

/** "9:00 AM – 10:15 AM" (en dash) from fake-local grid Dates. */
export const timeRangeLabel = (start: Date, end: Date): string =>
  `${wallClockLabel(start)} – ${wallClockLabel(end)}`;

const OWED = new Set(["unpaid", "partially_paid"]);

export interface BlockState {
  timeBlock: boolean;
  transient: boolean;
  requested: boolean;
  noShow: boolean;
  /** Cancelled / Declined. */
  voided: boolean;
  ended: boolean;
  /** Ended and still owed (not Completed). */
  overdue: boolean;
  /** Completed, or ended with nothing owed. */
  settled: boolean;
}

/** The block's look, from status, payment and time (salon "now"). */
export function blockState(event: CalendarEventType, now: Date): BlockState {
  const ended = event.end.getTime() <= now.getTime();
  if (event.isTimeBlock) {
    return {
      timeBlock: true,
      // A new block's preview while its drawer is open.
      transient: !!event.isTransient,
      requested: false,
      noShow: false,
      voided: false,
      ended,
      overdue: false,
      settled: false,
    };
  }
  const appt = event as CalendarEvent;
  const status = appt.status;
  const owed = OWED.has(appt.transactionStatus);
  const done = status === "Completed";
  return {
    timeBlock: false,
    transient: !!appt.isTransient,
    requested: status === "Requested",
    noShow: status === "No Show",
    voided: status === "Cancelled" || status === "Declined",
    ended,
    overdue: ended && !done && owed,
    settled: done || (ended && !owed),
  };
}

// ── Flags (chip slot) ─────────────────────────────────────────────────────

export type FlagTone = "alert" | "warn" | "info" | "good" | "muted";

export interface EventFlag {
  key: string;
  label: string;
  /** One-line explanation (drawer "Need to know" rows, chip tooltips). */
  detail?: string;
  Icon: (typeof DESIGN_ICONS)[keyof typeof DESIGN_ICONS];
  tone: FlagTone;
  /** Badge + Label mode: a short word beside the icon ("Any", "New"). These
   *  chips lead the row and keep their word wherever it fits (planChips). */
  chipLabel?: string;
}

/** Chip hover text: the label, then the explanation. */
export const flagTooltip = ({ label, detail }: EventFlag): string =>
  detail ? `${label} — ${detail}` : label;

/** Every badge with its icon, name and explanation (the "+N" chip's hover
 *  list). */
const FlagList = ({ flags }: { flags: EventFlag[] }) => (
  <Stack gap={8}>
    {flags.map((flag) => (
      <Group key={flag.key} gap={8} wrap="nowrap" align="flex-start">
        <Box
          component="span"
          className={styles.flag}
          data-tone={flag.tone}
          aria-hidden
        >
          <flag.Icon size={12} />
        </Box>
        <Box miw={0}>
          <Text size="xs" fw={600} lh={1.35}>
            {flag.label}
          </Text>
          {flag.detail && (
            <Text size="xs" lh={1.35} opacity={0.8}>
              {flag.detail}
            </Text>
          )}
        </Box>
      </Group>
    ))}
  </Stack>
);

const TOOLTIP_PROPS = {
  events: { hover: true, focus: true, touch: false },
  multiline: true,
  maw: 260,
  radius: 10,
  withinPortal: true,
} as const;

// Chip geometry for the fit check (EventBlock.module.css .flag /
// .flagLabel / .flagMore), px.
const CHIP_PX = 18;
const CHIP_GAP_PX = 3;
/** A labelled chip around its word: 4px padding each side, the 12px icon
 *  and the label's 2px margin. */
const LABELLED_CHROME_PX = 22;
/** The "+N" chip around its text: 4px padding + 1px border, each side. */
const MORE_CHROME_PX = 10;
/** Block width the chip row can't use: the 4px service bar + 4px on the
 *  left, the row's 5px inset on the right. */
const BLOCK_INSET_PX = 13;

/** Chip text: a Badge + Label word (9.5px bold caps) or "+N" (10px bold). */
export type ChipText = "label" | "more";
const CHIP_FONT_PX: Record<ChipText, number> = { label: 9.5, more: 10 };
/** Per-character estimate where canvas can't measure. */
const CHIP_CHAR_PX: Record<ChipText, number> = { label: 7.5, more: 6.5 };

let chipTextContext: CanvasRenderingContext2D | null | undefined;
let chipFontFamily = "sans-serif";

/** Rendered width (px) of a chip's text in the page font, measured on a
 *  canvas (the min-width day block leaves only a few px to spare). */
export function chipTextPx(text: string, kind: ChipText): number {
  if (chipTextContext === undefined) {
    chipTextContext =
      typeof document === "undefined"
        ? null
        : document.createElement("canvas").getContext("2d");
    chipFontFamily =
      (typeof document !== "undefined" &&
        getComputedStyle(document.body).fontFamily) ||
      chipFontFamily;
  }
  if (!chipTextContext) return text.length * CHIP_CHAR_PX[kind];
  chipTextContext.font = `700 ${CHIP_FONT_PX[kind]}px ${chipFontFamily}`;
  return chipTextContext.measureText(
    kind === "label" ? text.toUpperCase() : text,
  ).width;
}

export interface ChipPlan {
  shown: EventFlag[];
  /** Collapsed into the "+N" chip. */
  rest: EventFlag[];
  /** Badge + Label chips show their word. */
  labelled: boolean;
}

/**
 * Which chips show, given the block's width (px; null = unknown, e.g. List
 * / Card rows, where the prototype cap `max` counts chips instead).
 * Badge + Label chips ("Any", "New") lead the row and keep their word; the
 * other chips follow as icons, as many as fit, the rest collapsing into
 * "+N". Only when the labelled chips + "+N" can't fit does the row fall
 * back to icons alone (as many as fit, then "+N").
 */
export function planChips(
  flags: EventFlag[],
  max: number,
  width: number | null,
  textPx: (text: string, kind: ChipText) => number = chipTextPx,
): ChipPlan {
  const lead = flags.filter((f) => f.chipLabel);
  const ordered = [...lead, ...flags.filter((f) => !f.chipLabel)];

  const chipPx = (flag: EventFlag, labelled: boolean) =>
    labelled && flag.chipLabel
      ? LABELLED_CHROME_PX + textPx(flag.chipLabel, "label")
      : CHIP_PX;
  const morePx = (count: number) =>
    Math.max(CHIP_PX, MORE_CHROME_PX + textPx(`+${count}`, "more"));
  const plan = (count: number, labelled: boolean): ChipPlan => ({
    shown: ordered.slice(0, count),
    rest: ordered.slice(count),
    labelled,
  });
  const fits = ({ shown, rest, labelled }: ChipPlan) => {
    if (width === null) return shown.length + (rest.length ? 1 : 0) <= max;
    const chips = shown.map((f) => chipPx(f, labelled));
    if (rest.length) chips.push(morePx(rest.length));
    const row = chips.reduce((w, px) => w + px, 0);
    return row + CHIP_GAP_PX * (chips.length - 1) <= width - BLOCK_INSET_PX;
  };

  for (let n = ordered.length; n >= Math.max(1, lead.length); n--) {
    if (fits(plan(n, true))) return plan(n, true);
  }
  for (let n = ordered.length; n > 1; n--) {
    if (fits(plan(n, false))) return plan(n, false);
  }
  return plan(1, false);
}

/** The chip row's block (its offsetParent) width, kept live; null until
 *  measured or when not `enabled`. */
function useBlockWidth(enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const block = ref.current?.offsetParent;
    if (!enabled || !(block instanceof HTMLElement)) return;
    const measure = () => setWidth(block.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(block);
    return () => observer.disconnect();
  }, [enabled]);
  return [ref, width] as const;
}

/**
 * Flag chips, bottom-right of a block (or inline in List / Card). On grid
 * blocks (`fit`) the row fits the block's width (`planChips`); otherwise it
 * shows up to `max`. Collapsed chips become a "+N" chip whose tooltip lists
 * every badge. Badge + Label chips lead and show their word (`chipLabel`)
 * wherever it fits.
 */
export const EventFlags = ({
  flags,
  max = 4,
  muted = false,
  inline = false,
  fit = false,
  float = false,
  wrap = false,
}: {
  flags: EventFlag[];
  /** Chip cap when the width isn't measured (`fit` off, or not yet). */
  max?: number;
  /** Settled (past) blocks dim their chips. */
  muted?: boolean;
  /** List / Card rows: in flow instead of pinned to the corner. */
  inline?: boolean;
  /** Grid blocks: fit the chips to the block's measured width. */
  fit?: boolean;
  /** Grid blocks: float bottom-right as the first child of the text column,
   *  so the lines beside the chips truncate instead of running under them.
   *  The host sets --flags-nudge-x / -y (how far past its text box's right /
   *  bottom edge the chips sit). */
  float?: boolean;
  /** Inline rows that wrap onto more lines instead of collapsing. */
  wrap?: boolean;
}) => {
  const [ref, width] = useBlockWidth(fit && flags.length > 0);
  if (!flags.length) return null;
  const { shown, rest, labelled } = planChips(
    flags,
    wrap ? flags.length : max,
    fit ? width : null,
  );
  const className = inline
    ? styles.flagsInline
    : float
      ? styles.flagsFloat
      : styles.flags;
  const row = (
    <Box
      ref={ref}
      className={className}
      data-muted={muted || undefined}
      data-wrap={wrap || undefined}
      data-testid="event-flags"
    >
      {shown.map((flag) => {
        const chipLabel = labelled ? flag.chipLabel : undefined;
        return (
          <Tooltip key={flag.key} label={flagTooltip(flag)} {...TOOLTIP_PROPS}>
            <Box
              component="span"
              className={styles.flag}
              data-tone={flag.tone}
              data-labelled={chipLabel ? true : undefined}
              role="img"
              aria-label={flag.label}
              tabIndex={0}
            >
              <flag.Icon size={12} />
              {chipLabel && (
                <Box component="span" className={styles.flagLabel} aria-hidden>
                  {chipLabel}
                </Box>
              )}
            </Box>
          </Tooltip>
        );
      })}
      {rest.length > 0 && (
        <Tooltip
          label={<FlagList flags={[...shown, ...rest]} />}
          {...TOOLTIP_PROPS}
        >
          <Box
            component="span"
            className={`${styles.flag} ${styles.flagMore}`}
            role="img"
            aria-label={flags.map((f) => f.label).join(", ")}
            tabIndex={0}
          >
            {`+${rest.length}`}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
  if (!float) return row;
  // The zero-width spacer floats first, so the chips clear it and sit at the
  // bottom of the text column.
  return (
    <>
      <Box className={styles.flagsSpacer} aria-hidden />
      {row}
    </>
  );
};

/** Prototype flag caps: fewer chips as lanes get narrower (mobile too). */
export const flagCap = (mode: BlockMode, laneCount: number) =>
  mode === "week"
    ? laneCount > 1
      ? 1
      : 2
    : laneCount > 2
      ? 1
      : laneCount > 1
        ? 2
        : 4;

// ── Prop getter (the look) ────────────────────────────────────────────────

/**
 * RBC `eventPropGetter`: block classes + the service colour var. Called in
 * the same render as the lane layout, so lane density is known here.
 */
export const makeEventPropGetter =
  (timezone: string | undefined, mode: BlockMode) =>
  (event: CalendarEventType) => {
    const s = blockState(event, getSalonNow(timezone));
    const appt = event as CalendarEvent;
    const lanes = laneMeta(event)?.laneCount ?? 1;
    const overflow = laneOverflow(event);
    const classes = [
      styles.block,
      mode === "week" ? styles.week : styles.day,
      s.timeBlock && styles.timeBlock,
      s.timeBlock && s.ended && styles.timeBlockPast,
      s.timeBlock && s.transient && styles.timeBlockDraft,
      !s.timeBlock && s.transient && styles.transient,
      !s.timeBlock &&
        s.transient &&
        appt.isActiveTransient &&
        styles.transientActive,
      // A draft row with no service yet: the dotted ghost box.
      !s.timeBlock && s.transient && !appt.salonServiceId && styles.placeholder,
      !s.transient && s.requested && styles.requested,
      s.noShow && styles.noShow,
      s.voided && styles.voided,
      !s.noShow && !s.voided && s.overdue && styles.overdue,
      !s.noShow && !s.voided && s.settled && styles.settled,
      mode === "week" && lanes > 1 && styles.dense,
      event.end.getTime() - event.start.getTime() <= SHORT_MS && styles.short,
      overflow?.kind === "more" && styles.laneMore,
      overflow?.kind === "hidden" && styles.laneHidden,
    ].filter(Boolean);
    const style = s.timeBlock
      ? {}
      : ({
          "--svc": appt.backgroundColor || FALLBACK_SERVICE_COLOR,
        } as React.CSSProperties);
    return { className: classes.join(" "), style };
  };

// ── Content ───────────────────────────────────────────────────────────────

const serviceAndClient = (event: CalendarEventType) => {
  const [service, fromTitle] = event.title.split(" - ");
  const appt = event as CalendarEvent;
  const client =
    appt.firstName && appt.lastName
      ? `${appt.firstName} ${appt.lastName}`
      : fromTitle || "";
  return { service: service || "", client };
};

const TimeBlockContent = ({ event }: { event: CalendarEventType }) => {
  const block = event as CalendarEventType & {
    type?: string;
    description?: string;
    recurrenceParentId?: string;
    recurrenceInterval?: number;
  };
  const Icon = timeBlockIcon(block.type);
  const label = `${block.type || "Time block"}${block.description ? ` · ${block.description}` : ""}`;
  const repeats = !!(block.recurrenceParentId || block.recurrenceInterval);
  return (
    <Box className={styles.row}>
      <Box className={styles.content}>
        <Box component="span" className={styles.client}>
          <Box component="span" className={styles.blockIcon}>
            <Icon size={13} />
          </Box>
          {label}
        </Box>
        <Box component="span" className={`${styles.time} ${styles.blockTime}`}>
          {timeRangeLabel(event.start, event.end)}
          {repeats ? " · Repeats" : ""}
        </Box>
      </Box>
    </Box>
  );
};

/** RBC `event` component (module level; data from context + the event). */
export const EventBlock = ({ event }: { event: CalendarEventType }) => {
  const { mode, timezone, initialsFor, photoFor, flagsFor } =
    useContext(EventBlockContext);

  const overflow = laneOverflow(event);
  if (overflow?.kind === "more") {
    return <Box className={styles.laneMoreLabel}>{`+${overflow.count}`}</Box>;
  }
  if (event.isTimeBlock) return <TimeBlockContent event={event} />;

  const now = getSalonNow(timezone);
  const s = blockState(event, now);
  const appt = event as CalendarEvent;
  const { service, client } = serviceAndClient(event);
  const lanes = laneMeta(event)?.laneCount ?? 1;
  const initials = initialsFor?.(event) ?? null;
  const photo = initials ? (photoFor?.(event) ?? null) : null;
  const flagProps = {
    fit: true,
    flags: appt.isTransient ? [] : (flagsFor?.(event) ?? []),
    max: flagCap(mode, lanes),
    muted: s.settled,
  };

  // Processing: a near-white band over the processing minutes (any service
  // with processing time; not on settled blocks).
  const total = appt.duration || 0;
  const band =
    !s.settled && !s.transient && appt.processingTime > 0 && total > 0 ? (
      <Box
        className={styles.processing}
        style={{
          top: `${(Math.min(appt.baseDuration || 0, total) / total) * 100}%`,
          height: `${(appt.processingTime / total) * 100}%`,
        }}
        aria-hidden
      />
    ) : null;

  const requestPill =
    s.requested && !s.transient ? (
      <Box component="span" className={styles.requestPill}>
        Request
      </Box>
    ) : null;

  // Week, 2+ lanes: just the initials chip (+ start time at 2 lanes).
  if (mode === "week" && lanes > 1) {
    return (
      <Box className={styles.denseRow}>
        {band}
        {requestPill}
        {initials && (
          <Box component="span" className={styles.initials}>
            {photo ? (
              <Image
                src={photo.src}
                alt={photo.name}
                radius={6}
                className={styles.initialsPhoto}
              />
            ) : (
              initials
            )}
          </Box>
        )}
        {lanes === 2 && (
          <Box component="span" className={styles.chipTime}>
            {wallClockLabel(event.start).replace(/ [AP]M$/, "")}
          </Box>
        )}
        <EventFlags {...flagProps} />
      </Box>
    );
  }

  return (
    <>
      {band}
      {requestPill}
      <Box className={styles.row}>
        {initials && (
          <Box component="span" className={styles.initials}>
            {photo ? (
              <Image
                src={photo.src}
                alt={photo.name}
                radius={6}
                className={styles.initialsPhoto}
              />
            ) : (
              initials
            )}
          </Box>
        )}
        <Box className={styles.content}>
          <EventFlags {...flagProps} float />
          <Box component="span" className={styles.service}>
            {service}
          </Box>
          <Box component="span" className={styles.client}>
            <Box component="span" className={styles.inlineService}>
              {`${service} · `}
            </Box>
            {client}
          </Box>
          <Box component="span" className={styles.time}>
            {timeRangeLabel(event.start, event.end)}
          </Box>
        </Box>
      </Box>
    </>
  );
};
